import { BoxGeometry, Group, Mesh } from 'three';
import type { HouseConfig } from '@/model/schema';
import { createMaterialKit, type MaterialKit } from '../materials';
import type { HingeDef } from '../hinges';
import { buildBasePlate } from './buildBasePlate';
import { buildStory } from './buildStory';
import { buildRoof } from './buildRoof';
import { buildFence } from './buildFence';
import { buildMailbox } from './buildMailbox';
import { buildStairFlight } from './buildStairs';
import { buildDeck } from './buildDeck';
import { buildBalcony } from './buildBalcony';
import { buildTerrace } from './buildTerrace';
import { buildBasement } from './buildBasement';
import { buildGarage } from './buildGarage';
import { buildDriveway } from './buildDriveway';
import { buildWalkway } from './buildWalkway';
import type { HingeCollector } from './buildWalls';

/**
 * Top-level house builder. Produces a `HouseRoot` group with the scene-graph
 * structure specified in design doc §"Geometry Generation Approach":
 *
 *   HouseRoot
 *     BasePlate
 *     HouseBody
 *       Story_0
 *         ExteriorWalls / InteriorWalls / Windows / Doors / FloorSlab
 *       Story_1
 *       ...
 *     Roof
 *     Fence
 *     Mailbox
 *
 * The returned handle bundles the group together with the material kit it
 * owns so the caller can dispose both together when swapping in a new config.
 */
export interface BuiltHouse {
  group: Group;
  kit: MaterialKit;
  /** Every door / window leaf that was wrapped in a swing pivot during
   *  this build. The App pushes these into `useHingesStore` so the
   *  right-side panel can render a slider per leaf. */
  hinges: HingeDef[];
  dispose(): void;
}

export function buildHouse(cfg: HouseConfig): BuiltHouse {
  const kit = createMaterialKit(cfg.palette, cfg.textures);

  const root = new Group();
  root.name = 'HouseRoot_Built';

  const hingeDefs: HingeDef[] = [];
  const hinges: HingeCollector = {
    add(def) {
      hingeDefs.push(def);
    },
  };

  root.add(buildBasePlate(cfg, kit));

  // Plinth (optional): a thin slab sitting on the base plate that lifts the
  // entire HouseBody. When present, we also generate a small front stair
  // up to the door.
  const plinthHeight = cfg.house.plinthHeight;
  if (plinthHeight > 0.001) {
    const plinth = new Mesh(
      new BoxGeometry(
        cfg.house.footprintWidth + 2 * PLINTH_OVERHANG,
        plinthHeight,
        cfg.house.footprintDepth + 2 * PLINTH_OVERHANG,
      ),
      kit.basePlate,
    );
    plinth.name = 'Plinth';
    plinth.castShadow = true;
    plinth.receiveShadow = true;
    plinth.position.set(
      cfg.house.originX + cfg.house.footprintWidth / 2,
      plinthHeight / 2,
      cfg.house.originZ + cfg.house.footprintDepth / 2,
    );
    root.add(plinth);

    if (cfg.house.hasFrontSteps) {
      root.add(buildFrontSteps(cfg, kit, plinthHeight));
    }
  }

  const body = new Group();
  body.name = 'HouseBody';
  body.position.y = plinthHeight;
  // Story N sits at y = N*storyHeight and its walls span [0, storyHeight]
  // in story-local space. The roof therefore sits at exactly N*storyHeight,
  // NOT at floorThickness + N*storyHeight — that extra offset produces a
  // visible gap above the top-story walls.
  const topElevation = cfg.stories.length * cfg.house.storyHeight;
  const decor = { windowMullion: cfg.style.windowMullion };
  const terraces = cfg.terraces ?? [];
  for (const story of cfg.stories) {
    body.add(buildStory(cfg.house, story, kit, decor, hinges, terraces));
  }

  // Recessed terraces (cut-in notches). Each terrace lives at its host
  // story's floor elevation and is attached under HouseBody (just like
  // balconies) so it disappears / reappears in lockstep with peeling.
  // The wall builder has already skipped the host-facade segment for us.
  for (const terrace of terraces) {
    const story = cfg.stories[terrace.level];
    if (!story) continue;
    const storyFloorTopY = story.elevation + cfg.house.floorThickness / 2;
    const terraceGroup = buildTerrace(
      cfg.house,
      terrace,
      storyFloorTopY,
      cfg.house.storyHeight,
      kit,
      hinges,
    );
    terraceGroup.name = `Terrace_L${terrace.level}`;
    // Match the X/Z offset that `buildStory` and `buildStairFlight`
    // apply so body-local (W/2, 0, D/2) lands at the house's actual
    // footprint centre in world space. Without this, the terrace's
    // interior-boundary walls and L-shaped railing render at the lot
    // origin instead of tracking the body — which looked like the
    // terrace had been "shifted into the building".
    terraceGroup.position.x = cfg.house.originX + cfg.house.footprintWidth / 2;
    terraceGroup.position.z = cfg.house.originZ + cfg.house.footprintDepth / 2;
    body.add(terraceGroup);
  }

  // Stair flights live in the body group so they share the plinth lift.
  for (const stair of cfg.stairs) {
    const flight = buildStairFlight(cfg.house, stair, kit);
    flight.position.x = cfg.house.originX + cfg.house.footprintWidth / 2;
    flight.position.z = cfg.house.originZ + cfg.house.footprintDepth / 2;
    body.add(flight);
  }

  root.add(body);

  const roofGroup = buildRoof(cfg.house, cfg.roof, topElevation, kit, terraces);
  roofGroup.position.y += plinthHeight;
  root.add(roofGroup);

  // Decks (ground-level patios) attach to the house body but live outside
  // its walls; the deck slab top is set to the L0 indoor floor top so
  // crossing the door is flush. We pass lot bounds so the deck builder
  // can clamp its slab + stairs to stay inside the base plate (user
  // reported decks spilling onto the void).
  const groundFloorTopY = plinthHeight + cfg.house.floorThickness;
  const lotBounds = {
    xLo: -cfg.lot.width / 2,
    xHi: cfg.lot.width / 2,
    zLo: -cfg.lot.depth / 2,
    zHi: cfg.lot.depth / 2,
  };
  for (const deck of cfg.decks) {
    root.add(buildDeck(cfg.house, deck, groundFloorTopY, lotBounds, kit));
  }

  // Balconies are per-story; the elevation we pass is the indoor FLOOR
  // TOP for that story so the balcony slab lines up flush — no step over
  // the door threshold. For an upper story whose slab is centred on
  // story.elevation with thickness floorThickness, that's
  // storyElevation + floorThickness/2 (plus the plinth lift).
  // We name the wrapping group `Balcony_L<level>` and attach it under
  // the body group; the scene's peel pass treats it as belonging to that
  // story so it disappears in lockstep with the floor it connects to.
  for (const story of cfg.stories) {
    if (!story.balcony) continue;
    const floorTopY = plinthHeight + story.elevation + cfg.house.floorThickness / 2;
    const balc = buildBalcony(cfg.house, story.balcony, floorTopY - plinthHeight, kit);
    balc.name = `Balcony_L${story.level}`;
    body.add(balc);
  }

  if (cfg.garage) {
    // Garages sit at true grade (y=0) regardless of plinth lift, so we
    // parent to `root` rather than the (lifted) body group.
    root.add(buildGarage(cfg.house, cfg.garage, kit, hinges));
  }

  if (cfg.driveway) {
    root.add(buildDriveway(cfg.driveway, kit));
  }

  // Front walkway (door → gate or door → lot front edge). Always
  // generated; the cost is two slabs and the visual is a strict
  // improvement over a bare lawn between door and street.
  root.add(buildWalkway(cfg, kit));

  if (cfg.basement) {
    const basement = buildBasement(cfg.house, cfg.basement, kit);
    // Basements live OUTSIDE `body` (which is lifted by plinthHeight
    // and whose children represent above-grade stories). Adding to
    // `root` keeps the basement fixed at true below-grade elevation
    // regardless of plinth lifts, and it stays visible during all
    // peel operations that target above-grade stories.
    root.add(basement);
  }

  if (cfg.fence) root.add(buildFence(cfg.lot, cfg.fence, kit, hinges, cfg.driveway));
  if (cfg.mailbox) root.add(buildMailbox(cfg.mailbox, kit));

  return {
    group: root,
    kit,
    hinges: hingeDefs,
    dispose() {
      root.traverse((obj) => {
        if ('geometry' in obj) {
          const geo = (obj as unknown as { geometry?: { dispose?: () => void } }).geometry;
          geo?.dispose?.();
        }
      });
      kit.dispose();
    },
  };
}

/**
 * Two or three small steps from grade up to the plinth, planted at the
 * front door's offset on the front facade. We don't actually peek at the
 * door config — the steps are wide enough to land roughly under it for any
 * reasonable centred entry. A future iteration can inspect cfg.stories[0].doors.
 */
/**
 * Plinth box overhangs the house footprint by this much on each side
 * (see buildHouse → "Plinth" mesh). We keep the constant in sync here
 * so the front steps land flush with the plinth's outer edge, not
 * embedded into it.
 */
const PLINTH_OVERHANG = 0.2;
/**
 * 1 mm separation everywhere a step face would otherwise be coplanar
 * with the plinth front, the base plate top, or the floor slab bottom.
 * Without this the GPU sees coincident polygons and the two faces
 * flicker / moire as the camera moves.
 */
const STEP_EPS = 0.001;

function buildFrontSteps(cfg: HouseConfig, kit: MaterialKit, plinthHeight: number): Group {
  const group = new Group();
  group.name = 'FrontSteps';
  const stepCount = Math.max(2, Math.round(plinthHeight / 0.15));
  const stepRise = plinthHeight / stepCount;
  const stepDepth = 0.32;
  const door = cfg.stories[0]?.doors.find((d) => d.role === 'front');
  const doorOffset = door ? door.offset : (cfg.house.footprintWidth - 1.6) / 2;
  const doorWidth = door ? door.width : 1.0;
  const stepWidth = Math.max(1.4, doorWidth + 0.4);
  const frontWorldX = cfg.house.originX + doorOffset + doorWidth / 2;
  // Land the topmost tread's BACK face exactly STEP_EPS in front of the
  // plinth's outer edge so the two surfaces never share a plane. Shifting
  // `frontWorldZ` outward by (PLINTH_OVERHANG + STEP_EPS) walks the whole
  // run away from the plinth in lockstep.
  const frontWorldZ = cfg.house.originZ - PLINTH_OVERHANG - STEP_EPS;

  for (let i = 0; i < stepCount; i++) {
    const tread = new Mesh(
      new BoxGeometry(stepWidth, stepRise, stepDepth),
      kit.stair,
    );
    tread.castShadow = true;
    tread.receiveShadow = true;
    // Lift everything by STEP_EPS so the bottom step doesn't kiss the base
    // plate top (another z-fight source). Bottom step bottom now sits at
    // y = STEP_EPS, top step top at y = plinthHeight + STEP_EPS — still
    // inside the floor-slab thickness so it reads as a flush landing.
    tread.position.set(
      frontWorldX,
      STEP_EPS + (i + 0.5) * stepRise,
      frontWorldZ - stepDepth / 2 - (stepCount - 1 - i) * stepDepth,
    );
    group.add(tread);
  }
  return group;
}
